import {
  Box,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Heading,
  HStack,
  SimpleGrid,
} from "@chakra-ui/react";
import { HiOutlinePlus, HiOutlineTrash } from "react-icons/hi";
import CodesetSelect from "../../CodesetSelect";
import UuidPreview from "../../UuidPreview";
import CurrencyAmountInput from "../../CurrencyAmountInput";
import RateAmountInput from "../../RateAmountInput";
import { createEmptyCoverage, createEmptyDeductible } from "../../utils/placementDefaults";

const CoverageDeductibleItem = ({ item, itemIndex, onUpdate, onRemove, type }) => {
  const updateField = (field, value) => {
    onUpdate(itemIndex, field, value);
  };

  return (
    <Box borderWidth="1px" borderRadius="md" p={4} mb={4} borderColor="gray.300">
      <UuidPreview uuid={item.id} />

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
        <CodesetSelect
          label="Type"
          codesetCode={type === "coverage" ? "A22" : "A23"}
          value={item.type ? { code: item.type, description: "" } : null}
          onChange={(value) => updateField("type", value.code)}
        />

        <FormControl>
          <FormLabel>Description</FormLabel>
          <Input
            value={item.description || ""}
            onChange={(e) => updateField("description", e.target.value)}
          />
        </FormControl>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
        <CurrencyAmountInput
          label="Amount"
          value={item.amount}
          onChange={(value) => updateField("amount", value)}
        />

        <RateAmountInput
          label="Percentage"
          value={item.percentage}
          onChange={(value) => updateField("percentage", value)}
        />

        <FormControl>
          <FormLabel>Number of Lines</FormLabel>
          <Input
            type="number"
            value={item.numberOfLines}
            onChange={(e) => updateField("numberOfLines", parseInt(e.target.value) || 0)}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Number of Units</FormLabel>
          <Input
            type="number"
            value={item.numberOfUnits}
            onChange={(e) => updateField("numberOfUnits", parseInt(e.target.value) || 0)}
          />
        </FormControl>
      </SimpleGrid>

      <HStack justify="flex-end">
        <IconButton
          size="sm"
          icon={<HiOutlineTrash />}
          aria-label={`Delete ${type}`}
          onClick={() => onRemove(itemIndex)}
        />
      </HStack>
    </Box>
  );
};

export const CoveragesEditor = ({ coverages = [], onChange }) => {
  const updateCoverage = (coverageIndex, field, value) => {
    const updated = [...coverages];
    updated[coverageIndex] = {
      ...updated[coverageIndex],
      [field]: value,
    };
    onChange(updated);
  };

  const addCoverage = () => {
    onChange([...coverages, createEmptyCoverage()]);
  };

  const removeCoverage = (coverageIndex) => {
    const updated = [...coverages];
    updated.splice(coverageIndex, 1);
    onChange(updated);
  };

  return (
    <Box>
      <HStack alignItems="center" mb={4}>
        <Heading size="md">Coverages</Heading>
        <IconButton
          icon={<HiOutlinePlus />}
          size="md"
          aria-label="Add Coverage"
          onClick={addCoverage}
        />
      </HStack>

      {coverages.map((coverage, coverageIndex) => (
        <CoverageDeductibleItem
          key={coverage.id}
          item={coverage}
          itemIndex={coverageIndex}
          onUpdate={updateCoverage}
          onRemove={removeCoverage}
          type="coverage"
        />
      ))}
    </Box>
  );
};

export const DeductiblesEditor = ({ deductibles = [], onChange }) => {
  const updateDeductible = (deductibleIndex, field, value) => {
    const updated = [...deductibles];
    updated[deductibleIndex] = {
      ...updated[deductibleIndex],
      [field]: value,
    };
    onChange(updated);
  };

  const addDeductible = () => {
    onChange([...deductibles, createEmptyDeductible()]);
  };

  const removeDeductible = (deductibleIndex) => {
    const updated = [...deductibles];
    updated.splice(deductibleIndex, 1);
    onChange(updated);
  };

  return (
    <Box>
      <HStack alignItems="center" mb={4}>
        <Heading size="md">Deductibles</Heading>
        <IconButton
          icon={<HiOutlinePlus />}
          size="md"
          aria-label="Add Deductible"
          onClick={addDeductible}
        />
      </HStack>

      {deductibles.map((deductible, deductibleIndex) => (
        <CoverageDeductibleItem
          key={deductible.id}
          item={deductible}
          itemIndex={deductibleIndex}
          onUpdate={updateDeductible}
          onRemove={removeDeductible}
          type="deductible"
        />
      ))}
    </Box>
  );
};
